import { useState } from 'react'
import MFAccordion from '../../../../../../../components/ui-components/MFAccordion'
import type {
  LumpsumPlanRecommendationOverDiversification,
  LumpsumPlanRecommendationOverExposure
} from '../../api/recommendation.types'
import type { FundMissedGainsInfoParams } from '../missed-gains/FundMissedGainsInfoBottomSheet'
import {
  getPlanRecommendationTabFund,
  type PlanRecommendationPairView
} from '../../pair/planRecommendationPair.types'
import { EventNames } from '../../../../../../../types/misc.types'
import { trackEvent } from '../../../../../../../utils/appBridge'
import { PlansLumpsumSheetEventIds, PlansScreen } from '../../../../analytics/plansEvents'
import {
  buildReasonsSubtitle,
  ReasonsAccordionPlanType,
  renderReasonsToRebalancePointersForState,
  resolveReasonsToRebalanceState,
  type ReasonPointerData
} from './reasonsToRebalanceLogic'

export { ReasonsAccordionPlanType, ReasonsToRebalanceState } from './reasonsToRebalanceLogic'

const REASONS_ACCORDION_TITLES: Record<ReasonsAccordionPlanType, string> = {
  [ReasonsAccordionPlanType.REBALANCE]: 'Reasons to Rebalance',
  [ReasonsAccordionPlanType.REALLOCATE]: 'Reasons to Reallocate'
}

type ReasonsToRebalanceAccordionProps = {
  pair: PlanRecommendationPairView
  onMissedGainsInfoClick: (params: FundMissedGainsInfoParams) => void
  reasonsPlanType?: ReasonsAccordionPlanType
  defaultExpanded?: boolean
  /** Optional overrides when pair fields are not populated (e.g. future allocation rebalance cases). */
  overExposure?: LumpsumPlanRecommendationOverExposure | null
  overDiversification?: LumpsumPlanRecommendationOverDiversification | null
  analyticsScreen?: string
}

export function ReasonsToRebalanceAccordion({
  pair,
  onMissedGainsInfoClick,
  reasonsPlanType = ReasonsAccordionPlanType.REBALANCE,
  defaultExpanded = true,
  overExposure = null,
  overDiversification = null,
  analyticsScreen = PlansScreen.LumpsumAllocationRecommendation
}: ReasonsToRebalanceAccordionProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)
  const accordionTitle = REASONS_ACCORDION_TITLES[reasonsPlanType]

  const resolvedPair: PlanRecommendationPairView = {
    ...pair,
    overExposure: overExposure ?? pair.overExposure,
    overDiversification: overDiversification ?? pair.overDiversification
  }

  return (
    <MFAccordion
      title={accordionTitle}
      iconComponent={<ReasonsToRebalanceAccordionIcon />}
      isExpanded={isExpanded}
      onAccordionClick={() => {
        const tabFund = getPlanRecommendationTabFund(resolvedPair)
        const fundName = tabFund?.fundName ?? ''
        const fundIsin = tabFund?.isin ?? ''
        trackEvent({
          eventName: EventNames.UserClicked,
          payload: {
            ScreenName: analyticsScreen,
            eventId: PlansLumpsumSheetEventIds.ReasonsAccordion,
            buttonLabel: accordionTitle,
            ...(fundName ? { fundName } : {}),
            ...(fundIsin ? { fundIsin } : {})
          }
        })
        setIsExpanded((currentExpanded) => !currentExpanded)
      }}
      isBorderTop
      accordionHeaderClassName="!px-5"
      middleContent={(
        <div className="flex min-w-0 flex-col">
          <div className="text-[14px] font-[500] leading-[130%] text-[#232323]">{accordionTitle}</div>
          <div className="truncate text-[13px] font-[400] leading-[140%] text-[#6A6E6E]">
            {buildReasonsSubtitle(
              resolvedPair.underPerformance,
              resolvedPair.highExposure,
              resolvedPair.overExposure,
              resolvedPair.overDiversification,
              resolvedPair.exitingFund.categoryName,
              resolvedPair.enteringFund.categoryName
            )}
          </div>
        </div>
      )}
    >
      <div className="pb-4">
        <div className="flex flex-col divide-y divide-[#F0F4F7] [&>*]:py-4 [&>*:first-child]:pt-1">
          <ReasonsToRebalancePointers
            pair={resolvedPair}
            onMissedGainsInfoClick={onMissedGainsInfoClick}
          />
        </div>
      </div>
    </MFAccordion>
  )
}

type ReasonsToRebalancePointersProps = {
  pair: PlanRecommendationPairView
  onMissedGainsInfoClick: (params: FundMissedGainsInfoParams) => void
}

function ReasonsToRebalancePointers({
  pair,
  onMissedGainsInfoClick
}: ReasonsToRebalancePointersProps) {
  const reasonsState = resolveReasonsToRebalanceState(
    pair.underPerformance,
    pair.highExposure,
    pair.overExposure,
    pair.overDiversification
  )

  const reasonPointerData: ReasonPointerData = {
    pair,
    onMissedGainsInfoClick
  }

  return renderReasonsToRebalancePointersForState(reasonsState, reasonPointerData)
}

export function ReasonsToRebalanceAccordionIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden>
      <path
        d="M11.2188 23.7143V28.1071C11.2188 28.9159 11.8743 29.5714 12.683 29.5714H27.3259C28.1346 29.5714 28.7902 28.9159 28.7902 28.1071V23.7143C28.7902 22.9056 28.1346 22.25 27.3259 22.25H12.683C11.8743 22.25 11.2188 22.9056 11.2188 23.7143Z"
        stroke="#3D3D3D"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.2188 12.4643V16.8571C11.2188 17.6658 11.8743 18.3214 12.683 18.3214H27.3259C28.1346 18.3214 28.7902 17.6658 28.7902 16.8571V12.4643C28.7902 11.6556 28.1346 11 27.3259 11H12.683C11.8743 11 11.2188 11.6556 11.2188 12.4643Z"
        stroke="#3D3D3D"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M22.9375 29.5714V22.25" stroke="#3D3D3D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M17.0781 18.3214V11" stroke="#3D3D3D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
